/**
 * Good Atoms Mascots v4 — Revised SVG artwork + Framer Motion
 *
 * Shapes:    hexagon · blob · rounded-rect · gear · flower · tree
 * Emotions:  idle · happy · celebrating · sad · thinking · surprised
 *
 * v4:
 *  • New SVG artwork for all 6 mascots (Hex, Blobby, Pixel, Cogs, Blossom, Sprout)
 *  • Eyes are filled shapes — scaleY blink/wink animation
 *  • Mouth paths derived programmatically from artwork per emotion
 *  • Colour palette updated from design file
 */

import { motion, AnimatePresence, type TargetAndTransition, type Transition } from 'framer-motion'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useT } from '../../i18n'
import { useChildStore } from '../../store/childStore'

// ─── Types ────────────────────────────────────────────────────────────────────

export type MascotEmotion =
  | 'idle' | 'happy' | 'celebrating' | 'sad' | 'thinking' | 'surprised'

export type MascotSubject =
  | 'mathematics' | 'science' | 'technology' | 'engineering' | 'arts' | 'nature' | 'futureskills'

export interface MascotProps {
  subject:    MascotSubject
  emotion?:   MascotEmotion
  size?:      number
  className?: string
}

// ─── Design-system palette (from v4 artwork) ──────────────────────────────────

const C: Record<MascotSubject, { fill: string; stroke: string; face: string }> = {
  mathematics:  { fill: '#FDE35C', stroke: '#FD9E0A', face: '#FD9E0A' },
  science:      { fill: '#71FFDE', stroke: '#00D3A5', face: '#00D3A5' },
  technology:   { fill: '#7DBEF3', stroke: '#2970E0', face: '#2970E0' },
  engineering:  { fill: '#FDA984', stroke: '#E53F1F', face: '#E53F1F' },
  arts:         { fill: '#E3A1E8', stroke: '#954BAE', face: '#954BAE' },
  nature:       { fill: '#63C127', stroke: '#387C04', face: '#387C04' },
  futureskills: { fill: '#63C127', stroke: '#387C04', face: '#387C04' },
}

// ─── Blinking hook ────────────────────────────────────────────────────────────

function useBlinking(emotion: MascotEmotion) {
  const [blinking, setBlinking] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const active = emotion !== 'celebrating' && emotion !== 'surprised'

  useEffect(() => {
    if (!active) return
    function schedule() {
      timer.current = setTimeout(() => {
        setBlinking(true)
        timer.current = setTimeout(() => {
          setBlinking(false)
          schedule()
        }, 110)
      }, 2000 + Math.random() * 4000)
    }
    schedule()
    return () => {
      clearTimeout(timer.current)
      // Don't carry a mid-blink frame into the next activation
      setBlinking(false)
    }
  }, [active])

  return active && blinking
}

// ─── Scroll eye-offset hook ───────────────────────────────────────────────────

function useScrollEyeOffset(): number {
  const [offset, setOffset] = useState(0)
  useEffect(() => {
    const update = () => setOffset(Math.min(window.scrollY / 133, 6))
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [])
  return offset
}

// ─── Micro-animation hook ─────────────────────────────────────────────────────

type MicroEvent = 'none' | 'wink-a' | 'wink-b' | 'fidget'

function useMicroAnimation(emotion: MascotEmotion): MicroEvent {
  const [micro, setMicro] = useState<MicroEvent>('none')
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const active = emotion === 'idle' || emotion === 'happy'

  useEffect(() => {
    if (!active) return
    function schedule() {
      timer.current = setTimeout(() => {
        const pool: MicroEvent[] = ['wink-a', 'wink-b', 'fidget']
        const chosen = pool[Math.floor(Math.random() * pool.length)]
        setMicro(chosen)
        timer.current = setTimeout(() => {
          setMicro('none')
          schedule()
        }, chosen === 'fidget' ? 580 : 280)
      }, 8000 + Math.random() * 7000)
    }
    schedule()
    return () => {
      clearTimeout(timer.current)
      // Don't carry a mid-event frame into the next activation
      setMicro('none')
    }
  }, [active])

  return active ? micro : 'none'
}

// ─── Body animation per character + emotion ───────────────────────────────────

type MotionSpec = {
  animate:    TargetAndTransition
  transition: Transition
}

const FIDGET_MOTION: MotionSpec = {
  animate:    { rotate: [-7, 7, -4, 4, 0], y: [0, -5, 2, -3, 0] },
  transition: { duration: 0.55, ease: 'easeInOut' },
}

function bodyMotion(subject: MascotSubject, emotion: MascotEmotion): MotionSpec {
  if (emotion === 'celebrating') return {
    animate:    { y: [0, -18, 0, -12, 0], rotate: [-8, 8, -4, 4, 0], scale: [1, 1.13, 0.93, 1.07, 1] },
    transition: { duration: 0.65, repeat: Infinity, ease: 'easeInOut' },
  }
  if (emotion === 'sad') return {
    animate:    { y: [0, 4, 0], rotate: [-1.5, 1.5, -1.5], scaleY: [1, 0.97, 1] },
    transition: { duration: 3.8, repeat: Infinity, ease: 'easeInOut' },
  }
  if (emotion === 'thinking') return {
    animate:    { rotate: [-5, -7, -4, -6, -5], y: [0, -2, 0] },
    transition: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' },
  }
  if (emotion === 'surprised') return {
    animate:    { scale: [1.12, 1, 1.06, 1], y: [0, 5, 0] },
    transition: { duration: 0.45, repeat: 2, ease: 'easeOut' },
  }

  // idle / happy — per-character personality
  switch (subject) {
    case 'mathematics': return {
      animate:    { rotate: [-4, 4, -4], y: [0, -3, 0] },
      transition: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' },
    }
    case 'science': return {
      animate:    { y: [0, -9, 0], scaleX: [1, 1.025, 1], scaleY: [1, 0.975, 1] },
      transition: { duration: 4.6, repeat: Infinity, ease: 'easeInOut' },
    }
    case 'technology': return {
      animate:    { rotate: [-3, 3, -3], y: [0, -4, 0] },
      transition: { duration: 4.0, repeat: Infinity, ease: 'easeInOut' },
    }
    case 'engineering': return {
      animate:    { rotate: [-6, 6, -6], scale: [1, 1.03, 1] },
      transition: { duration: 2.9, repeat: Infinity, ease: 'easeInOut' },
    }
    case 'arts': return {
      animate:    { rotate: [-5, 5, -5], y: [0, -5, 0] },
      transition: { duration: 4.3, repeat: Infinity, ease: 'easeInOut' },
    }
    case 'nature':
    case 'futureskills': return {
      animate:    { rotate: [-2.5, 2.5, -2.5], y: [0, -4, 0], scaleX: [1, 1.015, 1] },
      transition: { duration: 5.5, repeat: Infinity, ease: 'easeInOut' },
    }
    default: return {
      animate:    { y: [0, -4, 0] },
      transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
    }
  }
}

// ─── Mouth paths per emotion ──────────────────────────────────────────────────
// Stroke-based curves derived from the filled mouth shapes in the v4 artwork.
// Coordinates match each mascot's SVG viewBox.

type MouthMap = Record<MascotEmotion, string>

// HEX — viewBox 0 0 160 173 — mouth spans x≈102–142, y≈111–129
const HEX_MOUTH: MouthMap = {
  idle:        'M103 117 C112 126 131 124 140 112',
  happy:       'M103 117 C112 126 131 124 140 112',
  celebrating: 'M101 117 C110 130 133 128 142 112',
  sad:         'M103 122 C113 113 131 113 140 119',
  thinking:    'M105 118 C116 117 131 116 139 116',
  surprised:   'M110 119 C114 127 130 127 134 119',
}

// BLOBBY — viewBox 0 0 193 162 — mouth spans x≈99–143, y≈97–117
const BLOB_MOUTH: MouthMap = {
  idle:        'M102 107 C109 116 134 116 141 106',
  happy:       'M102 107 C109 116 134 116 141 106',
  celebrating: 'M100 107 C107 120 135 120 143 105',
  sad:         'M102 112 C110 104 134 104 141 110',
  thinking:    'M104 109 C114 109 130 108 140 108',
  surprised:   'M108 109 C112 116 131 116 135 109',
}

// PIXEL — viewBox 0 0 164 158 — mouth spans x≈42–85, y≈115–134
const PIXEL_MOUTH: MouthMap = {
  idle:        'M44 124 C52 133 74 133 83 124',
  happy:       'M44 124 C52 133 74 133 83 124',
  celebrating: 'M41 124 C50 136 76 136 85 123',
  sad:         'M44 129 C52 121 74 121 83 127',
  thinking:    'M46 126 C56 125 72 124 81 124',
  surprised:   'M51 126 C54 133 73 133 77 126',
}

// COGS — viewBox 0 0 180 167 — mouth spans x≈69–114, y≈117–137
const GEAR_MOUTH: MouthMap = {
  idle:        'M71 124 C79 134 103 133 112 123',
  happy:       'M71 124 C79 134 103 133 112 123',
  celebrating: 'M68 124 C77 137 105 136 114 122',
  sad:         'M71 129 C80 121 103 121 112 127',
  thinking:    'M73 126 C83 126 101 124 111 124',
  surprised:   'M78 126 C82 133 101 133 106 126',
}

// BLOSSOM — viewBox 0 0 176 159 — mouth spans x≈48–90, y≈89–105
const BLOSSOM_MOUTH: MouthMap = {
  idle:        'M51 97 C58 105 80 105 87 96',
  happy:       'M51 97 C58 105 80 105 87 96',
  celebrating: 'M48 97 C56 109 82 109 90 95',
  sad:         'M51 102 C59 94 80 94 87 100',
  thinking:    'M53 99 C62 99 78 97 86 97',
  surprised:   'M58 99 C62 106 76 106 81 99',
}

// SPROUT — viewBox 0 0 186 168 — mouth spans x≈90–129, y≈81–102
const TREE_MOUTH: MouthMap = {
  idle:        'M92 91 C100 101 120 101 127 91',
  happy:       'M92 91 C100 101 120 101 127 91',
  celebrating: 'M89 91 C98 105 122 105 130 89',
  sad:         'M92 96 C101 88 120 88 127 94',
  thinking:    'M94 93 C104 93 118 91 126 91',
  surprised:   'M100 93 C103 100 117 100 121 93',
}

// ─── Mouth renderer — opacity crossfade ──────────────────────────────────────

function Mouth({ paths, emotion, face }: { paths: MouthMap; emotion: MascotEmotion; face: string }) {
  return (
    <>
      {(Object.entries(paths) as [MascotEmotion, string][]).map(([em, d]) => (
        <motion.path
          key={em}
          d={d}
          stroke={face}
          strokeWidth="5"
          strokeLinecap="round"
          fill="none"
          initial={{ opacity: em === 'idle' ? 1 : 0 }}
          animate={{ opacity: emotion === em ? 1 : 0 }}
          transition={{ duration: 0.18 }}
        />
      ))}
    </>
  )
}

// ─── SplitEyes — filled shapes, scaleY blink + independent wink ──────────────

interface SplitEyesProps {
  eyeA:     string
  eyeB:     string
  blinking: boolean
  micro:    MicroEvent
  face:     string
}

function SplitEyes({ eyeA, eyeB, blinking, micro, face }: SplitEyesProps) {
  const shared = {
    fill:       face,
    style:      { transformBox: 'fill-box' as const, transformOrigin: 'center' },
    transition: { duration: 0.07 },
  }
  return (
    <>
      <motion.path
        {...shared}
        d={eyeA}
        animate={{ scaleY: blinking || micro === 'wink-a' ? 0.05 : 1 }}
      />
      <motion.path
        {...shared}
        d={eyeB}
        animate={{ scaleY: blinking || micro === 'wink-b' ? 0.05 : 1 }}
      />
    </>
  )
}

// ─── Shared prop type ─────────────────────────────────────────────────────────

type SharedProps = {
  emotion: MascotEmotion
  size:    number
  fill:    string
  stroke:  string
  face:    string
}

// ─── Age-banded quip tables ───────────────────────────────────────────────────

type BilingualQuips = { no: string[]; en: string[] }

const YOUNG_QUIPS: Partial<Record<string, BilingualQuips>> = {
  hex: {
    no: ['Hei! Jeg er Hex! 🔢', 'To + to = fire! 🎉', 'En trekant har tre hjørner! 🔺', 'Trykk meg igjen! 😄', 'Hva er 5 + 1? 🤔'],
    en: ["Hi! I'm Hex! 🔢", 'Two + two = four! 🎉', 'A triangle has three corners! 🔺', 'Click me again! 😄', 'What is 5 + 1? 🤔'],
  },
  blobby: {
    no: ['Hei! Jeg er Blobby! 🔬', 'Maur kan løfte 50 ganger sin vekt! 🐜', 'Regnbuer er egentlig ringer! 🌈', 'Trykk meg igjen! 🧪', 'Blomster drikker vann gjennom stilken! 🌸'],
    en: ["Hi! I'm Blobby! 🔬", 'Ants can lift 50 times their weight! 🐜', 'Rainbows are actually full circles! 🌈', 'Click me again! 🧪', 'Flowers drink water through their stems! 🌸'],
  },
  pixel: {
    no: ['Hei! Jeg er Pixel! 💻', 'Datamaskiner snakker med nuller og enere! 0️⃣1️⃣', 'En robot kan danse! 🤖', 'Trykk meg igjen! 👾', 'Spill er laget av kode! 🎮'],
    en: ["Hi! I'm Pixel! 💻", 'Computers talk using zeros and ones! 0️⃣1️⃣', 'A robot can dance! 🤖', 'Click me again! 👾', 'Games are made of code! 🎮'],
  },
  cogs: {
    no: ['Hei! Jeg er Cogs! ⚙️', 'Hjul gjør det lettere å flytte ting! 🚗', 'Trekanter holder broer oppe! 🌉', 'Trykk meg igjen! 🔧', 'Et tau kan holde et tungt skip! ⚓'],
    en: ["Hi! I'm Cogs! ⚙️", 'Wheels make it easier to move things! 🚗', 'Triangles hold bridges up! 🌉', 'Click me again! 🔧', 'A rope can hold a heavy ship! ⚓'],
  },
  blossom: {
    no: ['Hei! Jeg er Blossom! 🎨', 'Rødt og gult lager oransje! 🍊', 'Musikk kan gjøre deg glad! 🎵', 'Trykk meg igjen! 🌸', 'Du kan tegne hva du føler! 🖍️'],
    en: ["Hi! I'm Blossom! 🎨", 'Red and yellow make orange! 🍊', 'Music can make you happy! 🎵', 'Click me again! 🌸', 'You can draw whatever you feel! 🖍️'],
  },
  sprout: {
    no: ['Hei! Jeg er Sprout! 🌳', 'Blader er grønne fordi de spiser sollys! ☀️', 'Jordbær er ikke egentlig bær! 🍓', 'Trykk meg igjen! 🍃', 'Regnormer hjelper planter å vokse! 🪱'],
    en: ["Hi! I'm Sprout! 🌳", 'Leaves are green because they eat sunlight! ☀️', "Strawberries aren't actually berries! 🍓", 'Click me again! 🍃', 'Earthworms help plants grow! 🪱'],
  },
}

const OLDER_QUIPS: Partial<Record<string, BilingualQuips>> = {
  hex: {
    no: ['Hei! Jeg er Hex. 🔢', 'RSA-kryptering bruker primtall med hundrevis av sifre.', 'Fibonacci-sekvensen dukker opp i DNA-spiraler. 🧬', "Eulers formel: e^(iπ) + 1 = 0. Universets vakreste ligning.", 'Pi gjentar seg aldri — uendelig, kaotisk, presist. 🥧'],
    en: ["Hi. I'm Hex. 🔢", 'RSA encryption uses prime numbers with hundreds of digits.', 'The Fibonacci sequence appears in DNA spirals. 🧬', "Euler's formula: e^(iπ) + 1 = 0. The universe's most beautiful equation.", 'Pi never repeats — infinite, chaotic, precise. 🥧'],
  },
  blobby: {
    no: ['Hei! Jeg er Blobby. 🔬', 'CRISPR lar oss redigere DNA som kode i en editor.', '90 % av cellene i kroppen din er bakterier. Hvem er du? 🦠', 'Kvantetunnelering er grunnen til at solen brenner.', 'Blekksprut har tre hjerter og blått blod. 🐙'],
    en: ["Hi. I'm Blobby. 🔬", 'CRISPR lets us edit DNA like code in a text editor.', '90% of cells in your body are bacteria. Who are you? 🦠', 'Quantum tunnelling is why the sun actually burns.', 'Octopuses have three hearts and blue blood. 🐙'],
  },
  pixel: {
    no: ['Hei! Jeg er Pixel. 💻', 'Sorteringsalgoritmer er en av informatikkens store skjønnheter.', "Moores lov: transistorer doblet seg hvert 2. år — inntil fysikken sa stopp.", 'LLM-er er matematisk tekstkomprimering med emergente egenskaper.', 'Det første programmet ble skrevet av Ada Lovelace i 1843. 👩‍💻'],
    en: ["Hi. I'm Pixel. 💻", "Sorting algorithms are one of CS's great beauties. Try quicksort.", "Moore's Law: transistors doubled every 2 years — until physics said stop.", 'LLMs are mathematical text compression with emergent properties.', 'The first program was written by Ada Lovelace in 1843. 👩‍💻'],
  },
  cogs: {
    no: ['Hei! Jeg er Cogs. ⚙️', 'Tensegrity-strukturer bruker strekk, ikke trykk — som et spindelvev.', 'James Watt patenterte dampmaskinen i 1769. Det startet en verden.', 'Materialtrøtthet er grunnen til at flyenes flytimer logges nøye.', 'Romfartøy navigerer med gravitasjonsslingring — gratis energi fra planeter. 🚀'],
    en: ["Hi. I'm Cogs. ⚙️", 'Tensegrity structures use tension, not compression — like a spider web.', 'James Watt patented the steam engine in 1769. It changed the world.', "Metal fatigue is why every aircraft's flight hours are carefully logged.", 'Spacecraft use gravitational slingshots — free energy from planets. 🚀'],
  },
  blossom: {
    no: ['Hei! Jeg er Blossom. 🎨', 'Da Vinci var ingeniør, anatom og maler — grensene er menneskeskapte.', 'Tolvtoneteknikk er musikk uten tonalt sentrum. Schönberg brøt alt.', 'Wabi-sabi: japansk estetikk som feirer ufullkommenhet og forgjengelighet.', 'Rothkos fargeblokker fikk folk til å gråte. Abstrakt kunst treffer noe dypt.'],
    en: ["Hi. I'm Blossom. 🎨", 'Da Vinci was an engineer, anatomist, and painter — boundaries are ours to set.', 'Twelve-tone music has no tonal centre. Schoenberg broke everything.', 'Wabi-sabi: Japanese aesthetics celebrating imperfection and transience.', "Rothko's colour fields made people cry. Abstract art hits something deep."],
  },
  sprout: {
    no: ['Hei! Jeg er Sprout. 🌳', 'Mykorrhiza-nettverk er skogens internett — trær handler med næringsstoffer.', 'Klimaendringer forskyver blomstringstider. Bestøvere henger etter. 🐝', 'Camouflage er ikke bare visuelt — bladinsekter kopierer til og med skjelving.', 'En furu i California er 5 000 år gammel. Den var et frø da Egypt ble bygget. 🌲'],
    en: ["Hi. I'm Sprout. 🌳", "Mycorrhizal networks are the forest's internet — trees trade nutrients.", 'Climate change shifts bloom times. Pollinators are falling behind. 🐝', 'Camouflage is not just visual — leaf insects even copy trembling in wind.', 'A pine in California is 5,000 years old. It was a seed when Egypt rose. 🌲'],
  },
}

// ─── Quips & interaction hook ─────────────────────────────────────────────────

const QUIP_KEYS: Record<MascotSubject, string> = {
  mathematics:  'hex',
  science:      'blobby',
  technology:   'pixel',
  engineering:  'cogs',
  arts:         'blossom',
  nature:       'sprout',
  futureskills: 'sprout',
}

function useMascotInteraction(
  subject:    MascotSubject,
  emotion:    MascotEmotion,
  size:       number,
  stroke:     string,
  face:       string,
  extraHover: Record<string, number> = {},
) {
  const { t, lang } = useT()
  const prefix   = QUIP_KEYS[subject]
  const ageBand  = useChildStore(s => s.activeChild?.ageBand)

  const quips = useMemo(() => {
    if (ageBand === '4-7') {
      const pool = YOUNG_QUIPS[prefix]
      if (pool) return lang === 'en' ? pool.en : pool.no
    }
    if (ageBand === '13-16') {
      const pool = OLDER_QUIPS[prefix]
      if (pool) return lang === 'en' ? pool.en : pool.no
    }
    return [0, 1, 2, 3, 4].map(i => t(`quip.${prefix}.${i}`))
  }, [t, prefix, ageBand, lang])

  const [hovered, setHovered]   = useState(false)
  const [quipIdx, setQuipIdx]   = useState(0)
  const [showQuip, setShowQuip] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const activeEmotion: MascotEmotion = showQuip ? 'celebrating' : emotion

  const scrollEyeY = useScrollEyeOffset()

  const handleClick = () => {
    clearTimeout(timer.current)
    setShowQuip(true)
    timer.current = setTimeout(() => {
      setShowQuip(false)
      setQuipIdx(i => (i + 1) % quips.length)
    }, 2800)
  }

  const bubble = (
    <AnimatePresence>
      {showQuip && (
        <motion.div
          key={quipIdx}
          initial={{ opacity: 0, scale: 0.7, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.7, y: 8 }}
          transition={{ type: 'spring', stiffness: 380, damping: 22 }}
          style={{
            position:      'absolute',
            bottom:        'calc(100% + 12px)',
            right:         '-8px',
            background:    '#FFFBEB',
            border:        `2.5px solid ${stroke}`,
            borderRadius:  16,
            padding:       '7px 14px',
            fontSize:      Math.max(11, Math.round(size * 0.15)),
            fontWeight:    700,
            color:         face,
            whiteSpace:    'nowrap',
            zIndex:        50,
            boxShadow:     '0 6px 18px rgba(0,0,0,0.11)',
            pointerEvents: 'none' as const,
          }}
        >
          {quips[quipIdx]}
          <span style={{
            position:    'absolute',
            top:         '100%',
            right:       22,
            width:       0,
            height:      0,
            borderLeft:  '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop:   `8px solid ${stroke}`,
          }} />
        </motion.div>
      )}
    </AnimatePresence>
  )

  const wrapProps = {
    style:        { width: size, height: size, cursor: 'pointer' as const },
    whileHover:   { scale: 1.1, ...extraHover, transition: { type: 'spring' as const, stiffness: 400, damping: 18 } },
    onHoverStart: () => setHovered(true),
    onHoverEnd:   () => setHovered(false),
    onClick:      handleClick,
  }

  return { hovered, activeEmotion, bubble, wrapProps, scrollEyeY }
}

// ═══════════════════════════════════════════════════════════════════════════════
// HEX — Mathematics  (hover: looks left)
// viewBox 0 0 160 173
// ═══════════════════════════════════════════════════════════════════════════════

function HexMascot({ emotion, size, fill, stroke, face }: SharedProps) {
  const { hovered, activeEmotion, bubble, wrapProps, scrollEyeY } =
    useMascotInteraction('mathematics', emotion, size, stroke, face)
  const blinking = useBlinking(activeEmotion)
  const micro    = useMicroAnimation(activeEmotion)
  const { animate, transition } = micro === 'fidget' ? FIDGET_MOTION : bodyMotion('mathematics', activeEmotion)

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      {bubble}
      <motion.div {...wrapProps} animate={animate} transition={transition}>
        <svg width="100%" height="100%" viewBox="0 0 160 173" fill="none" overflow="hidden">
          {/* Outer border */}
          <path
            d="M87.8208 171.263C82.3959 174.219 76.2162 173.079 71.0271 170.256L9.48176 136.779C4.37132 134.004 0.495252 129.436 0.455941 123.24L-6.67743e-05 51.8279C-0.04724 44.2724 3.86814 39.1069 10.2444 35.6318L71.491 2.18596C77.9694 -1.35204 85.0769 -0.377124 91.0522 3.5697L151.717 37.1413C157.48 40.3334 159.579 46.1829 159.579 52.5513L159.548 123.256C159.548 128.524 156.717 132.581 152.77 135.804L87.8129 171.255L87.8208 171.263Z"
            fill={stroke}
          />
          {/* Body fill */}
          <path
            d="M152.22 50.2159C152.22 47.3305 150.318 44.5866 147.794 43.1871L86.1384 9.01012C81.822 6.62001 78.2841 6.65146 73.9913 9.01012L14.152 41.8741C11.589 43.2815 7.53991 45.2706 7.54778 48.8086L7.61067 120.111C7.17039 124.333 8.6249 128.107 12.4459 130.19L73.3702 163.455C77.1126 165.932 81.2953 166.569 85.5959 164.218L146.701 130.851C150.428 128.814 152.354 126.086 152.346 121.676L152.22 50.2159Z"
            fill={fill}
          />
          {/* Face — hover: looks left, scroll drift */}
          <motion.g
            animate={{ x: hovered ? -10 : 0, y: scrollEyeY }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
          >
            <Mouth paths={HEX_MOUTH} emotion={activeEmotion} face={face} />
            <SplitEyes
              eyeA="M94.5902 96.3434C103.97 95.8245 99.1267 86.1619 104.418 85.4621C106.36 85.2027 108.829 86.6572 108.852 89.2596C108.892 95.51 104.339 100.652 98.9538 102.515C92.8763 104.622 86.1855 103.081 82.3173 97.6957C81.0515 95.9346 80.737 93.9612 82.278 92.3494C86.6887 87.7343 87.7658 96.7208 94.5902 96.3434Z"
              eyeB="M131.346 81.7198C140.451 78.3626 132.761 68.3855 140.183 69.2188C142.393 69.4704 143.611 71.2473 143.784 73.5509C144.248 79.707 140.671 84.9669 135.442 87.5771C130.127 90.2345 123.287 89.9515 118.774 85.5251C117.336 84.1177 117.1 82.0814 117.658 80.5011C118.098 79.2667 120.543 77.4663 122.069 78.7714C124.899 81.1851 127.682 83.0721 131.346 81.7198Z"
              blinking={blinking}
              micro={micro}
              face={face}
            />
          </motion.g>
        </svg>
      </motion.div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// BLOBBY — Science  (hover: looks up)
// viewBox 0 0 193 162
// ═══════════════════════════════════════════════════════════════════════════════

function BlobMascot({ emotion, size, fill, stroke, face }: SharedProps) {
  const { hovered, activeEmotion, bubble, wrapProps, scrollEyeY } =
    useMascotInteraction('science', emotion, size, stroke, face)
  const blinking = useBlinking(activeEmotion)
  const micro    = useMicroAnimation(activeEmotion)
  const { animate, transition } = micro === 'fidget' ? FIDGET_MOTION : bodyMotion('science', activeEmotion)

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      {bubble}
      <motion.div {...wrapProps} animate={animate} transition={transition}>
        <svg width="100%" height="100%" viewBox="0 0 193 162" fill="none" overflow="hidden">
          {/* Outer border */}
          <path
            d="M106.185 161.614C81.7137 163.073 54.3123 160.535 31.4429 151.489C8.73701 142.502 -5.40185 121.533 1.96187 97.2605C3.98264 90.602 7.20018 84.7743 10.9213 78.8942C21.6333 61.9734 17.134 53.2677 25.8711 34.9603C35.7133 14.3244 55.594 1.41308 78.3915 0.098395C95.6956 -0.902332 111.123 5.98502 122.168 19.0926C129.375 27.6478 139.446 32.0628 149.956 35.2023C175.604 42.8746 192.444 65.3615 192.987 91.969C193.556 120.133 177.919 141.384 151.996 151.895C137.34 157.84 122.038 160.673 106.192 161.614H106.185Z"
            fill={stroke}
          />
          {/* Body fill */}
          <path
            d="M182.406 72.085C176.311 56.4593 163.218 45.5952 147.189 40.9317C134.967 37.3736 124.281 31.7551 115.877 22.0291C110.161 15.4164 103.014 10.3539 94.2765 8.14972C74.69 3.21149 53.8152 9.62137 40.3042 24.6257C32.6462 33.1286 28.1992 43.4302 26.3746 54.6606C24.6612 65.2173 21.3783 74.7275 15.3552 83.5901C14.335 85.0879 10.2346 94.2319 10.2346 94.2319C2.16459 110.649 7.42252 129.002 23.1244 139.729C30.9197 145.053 39.3559 148.206 48.7143 150.39C76.1549 156.8 104.596 157.323 132.135 151.378C154.88 146.466 177.023 134.084 183.936 110.59C187.677 97.8816 187.232 84.4535 182.406 72.085Z"
            fill={fill}
          />
          {/* Face — hover: looks up, scroll drift */}
          <motion.g
            animate={{ y: (hovered ? -9 : 0) + scrollEyeY }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
          >
            <Mouth paths={BLOB_MOUTH} emotion={activeEmotion} face={face} />
            <SplitEyes
              eyeA="M92.6808 88.6134C101.614 89.359 99.4559 80.3329 104.308 81.0981C106.368 81.4252 108.114 83.5836 107.323 86.0298C105.355 92.093 99.3382 95.455 93.2693 95.4026C87.2005 95.3503 81.3605 92.0146 79.3724 85.9121C78.6792 83.7864 79.9022 81.7718 81.6483 81.2224C87.037 79.5284 84.1007 87.9005 92.6808 88.6134Z"
              eyeB="M151.636 88.548C160.511 89.5356 159.392 82.0334 162.374 81.2028C163.65 80.8496 165.049 81.1177 166.089 82.3212C166.991 83.3612 167.286 84.9048 166.612 86.5858C164.33 92.3023 158.614 95.383 152.676 95.3699C146.901 95.3634 141.101 92.322 138.864 86.5923C138.047 84.5058 138.576 82.3474 140.65 81.3728C145.41 79.1228 144.168 87.7173 151.636 88.548Z"
              blinking={blinking}
              micro={micro}
              face={face}
            />
          </motion.g>
        </svg>
      </motion.div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// PIXEL — Technology  (hover: looks right)
// viewBox 0 0 164 158
// ═══════════════════════════════════════════════════════════════════════════════

function PixelMascot({ emotion, size, fill, stroke, face }: SharedProps) {
  const { hovered, activeEmotion, bubble, wrapProps, scrollEyeY } =
    useMascotInteraction('technology', emotion, size, stroke, face)
  const blinking = useBlinking(activeEmotion)
  const micro    = useMicroAnimation(activeEmotion)
  const { animate, transition } = micro === 'fidget' ? FIDGET_MOTION : bodyMotion('technology', activeEmotion)

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      {bubble}
      <motion.div {...wrapProps} animate={animate} transition={transition}>
        <svg width="100%" height="100%" viewBox="0 0 164 158" fill="none" overflow="hidden">
          {/* Outer border */}
          <path
            d="M107.74 157.953L26.2995 157.935C12.3233 157.935 0.112578 147.475 0.100728 133.904L9.55728e-06 23.9597C-0.0118397 11.2615 10.9961 0.011873 23.9593 0.011873L140.118 0C154.213 0 164.024 11.8314 164 25.147L163.822 132.289C163.799 145.878 154.343 156.54 140.627 157.466C129.684 158.208 119.026 157.953 107.74 157.947V157.953Z"
            fill={stroke}
          />
          {/* Body fill */}
          <path
            d="M156.719 24.0903C156.707 15.7852 149.597 7.15349 140.372 7.15349L24.3739 7.11194C14.6931 7.11194 7.20432 15.708 7.23987 25.1767L7.62497 135.198C7.6546 143.628 16.3934 150.639 24.7235 150.621L139.822 150.419C149.959 150.401 156.594 141.758 156.612 132.087L156.76 52.2056L156.73 24.0903H156.719Z"
            fill={fill}
          />
          {/* Face — hover: looks right, scroll drift */}
          <motion.g
            animate={{ x: hovered ? 10 : 0, y: scrollEyeY }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
          >
            <Mouth paths={PIXEL_MOUTH} emotion={activeEmotion} face={face} />
            <SplitEyes
              eyeA="M40.5542 97.9939C40.5838 95.3165 42.3138 93.6187 44.6481 93.9214C46.8403 94.2064 48.4873 96.6522 47.7112 99.1812C45.6375 105.907 39.47 110.063 32.787 110.181C26.0211 110.3 19.5869 106.418 17.2881 99.8757C16.5476 97.7683 16.9741 95.9933 18.3723 94.705C19.4506 93.7136 22.6381 93.5059 23.2839 95.358C24.6347 99.2227 26.8683 101.817 30.8556 102.612C34.5407 103.343 38.0777 101.597 40.5542 97.9998V97.9939Z"
              eyeB="M108.385 98.6825C106.164 106.436 98.7223 110.734 91.038 109.914C84.4854 109.22 79.0229 105.088 77.5832 98.6409C77.1033 96.486 78.4422 94.4438 80.1189 94.0164C82.4947 93.4108 84.201 94.7406 84.8823 97.139C85.8184 100.463 89.4621 102.428 92.6081 102.606C95.754 102.785 99.6999 101.021 100.731 97.6614C101.442 95.3402 102.419 93.7492 104.949 93.8561C106.792 93.9333 109.156 95.9873 108.385 98.6765V98.6825Z"
              blinking={blinking}
              micro={micro}
              face={face}
            />
          </motion.g>
        </svg>
      </motion.div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// COGS — Engineering  (hover: looks left + slight rotate)
// viewBox 0 0 180 167
// ═══════════════════════════════════════════════════════════════════════════════

function GearMascot({ emotion, size, fill, stroke, face }: SharedProps) {
  const { hovered, activeEmotion, bubble, wrapProps, scrollEyeY } =
    useMascotInteraction('engineering', emotion, size, stroke, face, { rotate: 15 })
  const blinking = useBlinking(activeEmotion)
  const micro    = useMicroAnimation(activeEmotion)
  const { animate, transition } = micro === 'fidget' ? FIDGET_MOTION : bodyMotion('engineering', activeEmotion)

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      {bubble}
      <motion.div {...wrapProps} animate={animate} transition={transition}>
        <svg width="100%" height="100%" viewBox="0 0 180 167" fill="none" overflow="hidden">
          {/* Outer gear border */}
          <path
            d="M118.993 148.727C110.073 151.824 115.165 165.988 102.097 166.741C94.8443 167.16 87.4771 166.977 80.1785 166.833C66.152 166.551 69.9041 153.665 62.712 149.625C59.8352 148.012 56.692 148.279 53.8988 149.732L47.4678 153.071C43.4266 155.171 38.8602 154.623 35.07 151.9C29.7426 148.066 24.7271 143.73 20.2368 139.021C11.2106 129.542 23.5323 122.643 23.2963 116.184C23.1213 111.399 18.3494 108.729 13.8134 108.744C6.85728 108.775 1.07317 104.507 0.487152 97.3183C-0.182588 89.171 -0.144534 81.0085 0.509984 72.8537C1.52981 60.0508 12.7327 61.8993 19.0267 59.8911C22.8016 58.6891 24.3694 55.167 22.7408 51.5536C20.6859 46.9741 12.1924 39.5039 19.5443 31.1816C23.8291 26.3283 28.365 21.9313 33.3957 17.9223C39.0048 13.4493 45.8696 13.959 51.6004 18.0669C54.1272 19.885 57.3313 20.6229 60.4897 19.8546C70.4368 17.4431 64.6984 2.21356 77.4995 0.631268C85.0417 -0.304412 92.7437 -0.152269 100.339 0.661697C113.277 2.0462 108.171 16.6672 117.281 20.0219C126.2 23.3006 133.255 8.19279 145.775 18.0365C150.828 22.0074 155.478 26.2826 159.847 31.0219C169.124 41.1014 153.279 48.6172 156.757 56.4146C157.64 58.4001 159.611 59.9367 162.024 60.3703L170.243 61.8537C177.527 63.1621 180.266 69.7575 179.323 76.6344C179.612 74.512 180.48 91.8107 179.673 97.2802C177.481 112.114 162.815 105.306 158.196 112.342C155.889 115.857 157.883 120.193 160.41 123.091C164.512 127.808 165.205 133.901 160.897 138.587C156.453 143.433 151.627 147.921 146.201 151.595C134.07 159.796 128.034 145.578 119.001 148.72L118.993 148.727Z"
            fill={stroke}
          />
          {/* Body fill */}
          <path
            d="M172.184 73.6906C172.108 64.136 156.993 71.7659 150.372 59.0848C144.473 47.7882 158.751 40.8428 153.987 35.5939C149.839 31.022 145.105 27.0891 140.31 23.2855C134.138 18.3865 128.339 30.2308 115.713 26.6327C102.851 22.966 104.989 8.62651 100.408 7.98751C92.7589 6.9149 84.9427 6.72472 77.294 7.95708C72.8265 11.3499 74.1584 21.9314 64.3558 25.8035C51.2274 30.9916 45.0399 18.919 39.8799 22.6161C34.3013 26.6022 29.1869 31.4252 24.8412 36.773C21.2947 41.1319 33.8218 46.4341 30.1915 58.2708C29.0499 61.9983 25.4349 65.6802 20.6401 67.1712C14.4146 69.111 7.98359 67.7341 7.89987 72.9907C7.80854 78.795 6.88765 96.8163 9.64271 99.7451C11.7204 101.959 23.9584 99.9809 29.263 109.939C32.4291 115.887 30.2905 122.597 26.1959 127.488C24.0878 130.014 24.2324 132.73 26.5841 134.814L38.2208 145.16C44.4387 150.69 48.3125 140.071 60.3678 141.394C67.9633 142.231 73.4278 147.526 74.6531 154.867C75.1325 157.712 77.4386 159.378 80.2317 159.408L99.9205 159.575C102.843 159.598 105.141 158.031 105.682 154.669C106.96 146.704 113.117 141.569 120.896 140.991C132.357 140.139 135.85 151.101 142.852 144.825L154.086 134.753C160.76 128.774 147.677 125.191 150.356 113.134C151.163 109.513 154.093 105.496 158.317 103.358C165.327 99.8135 172.42 102.088 172.366 95.6829L172.184 73.7134V73.6906Z"
            fill={fill}
          />
          {/* Face — hover: looks left + rotate, scroll drift */}
          <motion.g
            animate={{ x: hovered ? -8 : 0, y: scrollEyeY }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
          >
            <Mouth paths={GEAR_MOUTH} emotion={activeEmotion} face={face} />
            <SplitEyes
              eyeA="M75.8784 101.312C75.0488 106.888 70.7716 110.129 65.8932 111.696C57.4073 114.427 48.0005 110.601 45.3444 101.913C44.6366 99.5929 46.1131 97.3184 47.9624 96.9304C54.3706 95.5764 50.6871 104.758 60.0406 105.26C69.6985 105.778 66.6086 96.5425 72.1872 96.9076C73.9377 97.0217 76.2665 98.6725 75.8708 101.312H75.8784Z"
              eyeB="M104.875 102.887C104.015 100.719 105.347 98.7866 106.298 98.0411C107.668 96.9609 110.614 96.7403 111.474 98.6649C113.049 102.187 115.051 104.971 119.054 105.321C129.526 106.242 126.253 96.1621 132.053 97.0294C134.61 97.4097 135.835 99.6614 135.097 102.453C133.72 107.702 129.145 110.836 123.879 112.046C116.207 113.803 107.973 110.677 104.875 102.887Z"
              blinking={blinking}
              micro={micro}
              face={face}
            />
          </motion.g>
        </svg>
      </motion.div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// BLOSSOM — Arts  (hover: looks right)
// viewBox 0 0 176 159
// ═══════════════════════════════════════════════════════════════════════════════

function BlossomMascot({ emotion, size, fill, stroke, face }: SharedProps) {
  const { hovered, activeEmotion, bubble, wrapProps, scrollEyeY } =
    useMascotInteraction('arts', emotion, size, stroke, face)
  const blinking = useBlinking(activeEmotion)
  const micro    = useMicroAnimation(activeEmotion)
  const { animate, transition } = micro === 'fidget' ? FIDGET_MOTION : bodyMotion('arts', activeEmotion)

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      {bubble}
      <motion.div {...wrapProps} animate={animate} transition={transition}>
        <svg width="100%" height="100%" viewBox="0 0 176 159" fill="none" overflow="hidden">
          {/* Outer flower border */}
          <path
            d="M87.7445 138.529C74.1065 160.705 42.9841 164.641 23.8806 150.081C10.1018 139.496 6.35149 121.058 14.9657 106.069C17.2312 102.127 20.0215 99.2024 23.2662 95.5801C15.8488 92.6106 10.6138 88.2459 6.26189 82.2364C-3.34425 68.9696 -1.82749 50.7109 10.3258 39.6264C21.0839 28.6571 39.4578 27.6203 53.1918 32.9962C53.9278 12.9519 70.0618 -1.05732 89.2933 0.0626501C107.213 1.10582 120.915 14.4623 122.073 32.977C130.854 30.2314 138.956 28.9259 147.448 30.4682C166.398 33.3738 179.332 51.1653 175.243 70C172.549 82.4092 163.474 91.5482 151.724 95.9065C165.342 107.215 168.638 125.954 159.947 140.763C153.106 151.79 141.663 157.55 128.838 158.554C112.153 159.86 97.0114 152.679 87.7317 138.529H87.7445Z"
            fill={stroke}
          />
          {/* Body fill */}
          <path
            d="M164.946 48.8165C161.029 42.6727 154.124 38.1096 145.887 36.6824C129.35 33.8153 120.556 43.1847 116.704 40.2343C113.856 38.0584 118.624 24.9387 106.182 13.6942C99.6162 7.76161 91.0276 5.64327 82.1318 7.14723C67.8346 9.56636 58.5613 22.4428 59.7452 36.8872C59.8732 38.4616 59.2845 39.6712 58.1773 40.4007C55.163 42.3847 45.8064 33.7705 30.0564 36.8488C16.9816 39.4024 6.50507 49.9685 6.76106 63.6705C7.02986 78.2493 18.8503 89.417 32.738 91.3882C34.2931 91.6058 34.3507 93.6409 34.2547 94.5177C33.9347 97.4616 25.5766 99.8103 20.4567 111.189C15.036 123.227 18.2104 136.603 29.1029 144.404C47.4 157.511 73.5689 151.201 84.3462 131.374C85.2294 129.749 85.9461 128.942 87.6741 128.878C89.3444 128.821 90.0932 129.736 91.13 131.566C101.818 150.484 125.523 156.999 143.903 145.889C154.392 139.547 159.25 127.963 156.376 115.944C154.45 107.887 149.119 101.404 142.495 96.5976C141.401 95.804 141.17 94.4601 141.317 93.4745C141.516 92.2137 142.623 91.5865 144.031 91.3178C154.245 89.3594 163.275 83.0684 167.371 73.2702C170.718 65.2513 169.771 56.3811 164.939 48.8037L164.946 48.8165Z"
            fill={fill}
          />
          {/* Face — hover: looks right, scroll drift */}
          <motion.g
            animate={{ x: hovered ? 8 : 0, y: scrollEyeY }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
          >
            <Mouth paths={BLOSSOM_MOUTH} emotion={activeEmotion} face={face} />
            <SplitEyes
              eyeA="M55.8349 72.6175C56.1549 75.4655 54.5934 78.3454 52.4622 80.2077C45.7744 86.0636 35.5027 85.43 29.5572 78.9086C28.1877 77.411 27.3237 75.7407 27.1061 73.8079C26.8565 71.6 27.9765 69.8656 29.8004 69.5648C35.3555 68.6625 32.0084 77.283 41.3393 77.5454C50.3119 77.8014 47.9887 69.872 52.2318 69.5968C53.723 69.5008 55.5853 70.416 55.8285 72.6111L55.8349 72.6175Z"
              eyeB="M110.912 72.9376C110.912 76.6047 108.582 79.2926 105.85 81.2125C97.837 86.8508 85.863 84.1629 82.2471 75.0495C81.3575 72.8032 82.599 70.4992 84.3142 69.8272C89.7669 67.7025 87.0341 77.1487 96.2499 77.5326C99.1938 77.6542 102.867 76.2399 103.981 73.0912C104.653 71.184 105.005 69.6801 107.117 69.5841C108.678 69.5137 110.912 70.3776 110.918 72.9312L110.912 72.9376Z"
              blinking={blinking}
              micro={micro}
              face={face}
            />
          </motion.g>
        </svg>
      </motion.div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// SPROUT — Nature  (hover: looks up)
// viewBox 0 0 186 168
// ═══════════════════════════════════════════════════════════════════════════════

function TreeMascot({ emotion, size, fill, stroke, face }: SharedProps) {
  const { hovered, activeEmotion, bubble, wrapProps, scrollEyeY } =
    useMascotInteraction('nature', emotion, size, stroke, face)
  const blinking = useBlinking(activeEmotion)
  const micro    = useMicroAnimation(activeEmotion)
  const { animate, transition } = micro === 'fidget' ? FIDGET_MOTION : bodyMotion('nature', activeEmotion)

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      {bubble}
      <motion.div {...wrapProps} animate={animate} transition={transition}>
        <svg width="100%" height="100%" viewBox="0 0 186 168" fill="none" overflow="hidden">
          {/* Outer border */}
          <path
            d="M130.239 158.307C121.733 170.155 73.0322 171.428 57.7028 160.54C49.3358 154.591 62.4805 143.073 48.0394 130.48C36.7554 132.893 24.9312 130.606 16.8583 122.527C10.9582 116.626 8.40731 108.799 10.2139 100.942C4.33186 96.3926 0.568527 90.3602 0.0703502 82.9053C-0.871984 68.8719 7.72906 56.0029 21.2699 51.4891C20.4776 46.3871 20.6336 41.8374 22.7584 37.1195C29.4328 22.3478 46.0106 14.0705 61.8563 18.9204C65.3735 10.8893 71.1776 5.51722 79.1364 2.40201C95.6062 -4.0385 114.603 2.80416 122.898 18.9564C140.424 13.5844 158.653 23.2421 164.079 40.5288C165.243 44.2383 164.637 47.5816 164.379 51.3871C176.815 55.7988 185.452 67.2452 185.536 80.3483C185.59 88.8056 181.941 95.9664 175.26 101.002C177.199 108.847 174.708 116.777 168.772 122.545C160.645 130.444 148.923 132.893 137.855 130.786C125.377 141.026 134.308 152.646 130.239 158.307Z"
            fill={stroke}
          />
          {/* Lower body fill */}
          <path
            d="M131.727 127.743C120.875 142.605 126.997 141.842 124.884 155.954C116.223 160.396 106.458 161.098 96.7945 161.446C85.2824 161.866 74.0404 161.086 63.2306 157.286C57.6906 155.342 62.2822 151.674 60.3855 139.027C59.7313 134.67 56.1 132.413 54.8456 127.617C60.2475 133.787 70.8953 136.278 72.84 136.65C80.2286 138.067 87.5332 136.098 92.581 135.63C98.7031 135.066 106.134 139.261 113.768 137.815C119.224 136.782 126.781 132.773 131.733 127.731L131.727 127.743Z"
            fill={fill}
          />
          {/* Main canopy fill */}
          <path
            d="M179.522 81.5909C179.708 68.2357 169.522 56.8733 155.844 55.4327C159.013 50.4268 159.805 45.0247 157.434 39.5685C150.85 24.4066 132.435 18.9025 118.204 27.888C117.94 22.2158 115.797 17.8521 111.848 14.0706C100.426 3.12836 82.3115 3.27841 71.4236 14.8569C67.9964 18.5063 66.4418 22.9061 66.3098 27.804C53.7053 18.6744 34.8046 25.253 28.4244 39.2564C25.9275 44.7305 26.1496 50.1386 29.3907 55.4807C17.7046 56.8192 8.73738 65.3606 6.40255 76.4529C4.45186 85.7265 8.2212 94.2438 17.2004 98.3734C13.6051 106.681 15.994 114.994 23.1245 120.198C32.7579 127.233 46.2507 127.461 56.4243 120.756C59.3353 124.346 62.2043 127.257 66.4538 129.448C75.5171 133.019 85.1085 132.413 93.4634 127.269C105.54 135.348 121.343 132.983 129.926 121.038C140.724 127.959 154.871 126.999 164.343 118.745C170.363 113.505 171.335 105.462 167.878 98.2654C174.708 95.2762 179.414 89.2859 179.522 81.5849V81.5909Z"
            fill={fill}
          />
          {/* Face — hover: looks up, scroll drift */}
          <motion.g
            animate={{ y: (hovered ? -7 : 0) + scrollEyeY }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
          >
            <Mouth paths={TREE_MOUTH} emotion={activeEmotion} face={face} />
            <SplitEyes
              eyeA="M92.0288 69.2502C91.7467 72.5274 89.1778 75.1445 86.4528 76.7591C76.6874 82.5573 62.4863 76.8311 63.0625 68.83C63.1886 67.0353 64.305 66.1409 65.5774 65.9189C69.917 65.1506 67.312 73.4518 77.2396 73.6679C87.9353 73.902 85.2764 65.6668 88.9857 65.7928C90.3362 65.8408 92.2389 66.8252 92.0288 69.2562V69.2502Z"
              eyeB="M156.822 68.2897C157.26 70.8948 155.753 73.4938 153.617 75.2525C147.35 80.4145 138.185 80.5225 131.745 75.5946C129.74 74.064 128.228 71.9092 127.916 69.5142C127.633 67.3534 128.72 66.0569 130.37 65.8348C134.896 65.2346 131.709 73.3017 142.075 73.5718C152.194 73.8359 150.616 66.2309 153.154 65.7868C154.745 65.5107 156.468 66.2249 156.816 68.2897H156.822Z"
              blinking={blinking}
              micro={micro}
              face={face}
            />
          </motion.g>
        </svg>
      </motion.div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════════════════════════

export function Mascot({ subject, emotion = 'idle', size = 80, className }: MascotProps) {
  const { fill, stroke, face } = C[subject]
  const p: SharedProps = { emotion, size, fill, stroke, face }

  return (
    <div className={className} style={{ display: 'inline-block' }}>
      {(subject === 'mathematics')                      && <HexMascot     {...p} />}
      {(subject === 'science')                          && <BlobMascot    {...p} />}
      {(subject === 'technology')                       && <PixelMascot   {...p} />}
      {(subject === 'engineering')                      && <GearMascot    {...p} />}
      {(subject === 'arts')                             && <BlossomMascot {...p} />}
      {(subject === 'nature' || subject === 'futureskills') && <TreeMascot    {...p} />}
    </div>
  )
}
